import { XHR_CONTENT_TYPE } from "Util/util.js";

export function getControllerMethods({ transformActionUrls, sendXhr, getXhrHeaders, UploadFileUrl, ProgramId }) {
    /**
     * Checks if the given object has an ActionUrls object.
     * If it exsists, it uses the information within to create new method properties on the object.
     * These methods perform ajax requests to the provided urls.
     * @param {object} object - the object for which action methods will be created.
     * @returns {nothing}
     */
    function connectObject(object) {
        // If ActionUrls is defined, that means that the urls to call each
        // available action have been hooked up during page rendering.
        //We can now dynamically create functions that exercise those urls
        if (object.ActionUrls) {
            const controller = getConnectedController(object.ActionUrls);

            for (const actionMethod in controller) {
                if (controller.hasOwnProperty(actionMethod)) {
                    object[actionMethod] = controller[actionMethod];
                }
            }
        }
    }

    function getConnectedController(actionUrls) {
        const controller = {};
        if (typeof transformActionUrls === "function") {
            transformActionUrls(actionUrls);
        }

        for (const prop in actionUrls) {
            // Loop through each of the defined action urls and create a
            // new function that encloses the correct parameters
            // This enables calling of MVC controller actions with more
            // natural syntax (and automatic ajax result checking) e.g.:
            // controller.ExampleAction({data:"some data"}).then(...do something...);
            // or let result  = await controller.ExmapleAction(...); ...do something as opposed to
            // $.ajax(createAjaxObject(exampleActionUrl, {data:"some data"})
            // .then(...check ajax result and also do something...)
            if (actionUrls.hasOwnProperty(prop) && prop[0] != "_") {
                controller[prop] = createActionMethod(actionUrls[prop]);
            }
        }
        return controller;
    }

    /**
     * The following function will create a new closure that encloses url and csrf token/nonce.
     * When called, the created closure executes an ajax call and returns the deferred object.
     * This allows for promise chaining.
     * It also performs some mininal boilerplate ajax result checking.
     * @param {string} url - Url for the ajax action
     * @returns {function} function(data) enclosing the url to use in an ajax request.
     * The returned function expects up to one data parameter to pass along to the request.
     */
    function createActionMethod(url) {
        const headers = getXhrHeaders();
        return async (data, options) => {
            const sync = options && options.sync;
            const result = await sendXhr({ url, data, sync: Boolean(sync), headers });
            return result;
        };
    }

    /**
     * @param {{file:File, isTemporary:Boolean}} options
     * @returns {Promise<{DocumentId:String}>}
     */
    async function uploadFile({ file, isTemporary, captureXhr }) {
        const data = {
            uploadFile: file,
            programId: ProgramId,
            isTemporary
        };

        const result = await sendXhr({
            url: UploadFileUrl,
            data,
            headers: getXhrHeaders(),
            captureXhr,
            contentType: XHR_CONTENT_TYPE.FormData
        });

        return result.Data;
    }

    return {
        connectObject,
        getConnectedController,
        createActionMethod,
        uploadFile
    };
}
